import React, { useState } from "react";
import { LoadingOutlined, SearchOutlined, DeleteOutlined } from "@ant-design/icons";

export default function HybridHistoryTable({
  loadingList = false,
  hybridSimulations = [],
  bessSimulations = [],
  onLoadSimulation,
  onViewResults,
  onDeleteSimulation,
}) {
  const [searchQuery, setSearchQuery] = useState("");

  const getHybridSimName = (s) => {
    if (s.name) return s.name.replace(/_hybrid$/i, "");
    const baseSim = bessSimulations?.find((b) => b.id === parseInt(s.bess_sizing_simulation));
    const baseName =
      s.bess_sizing_simulation_name ||
      baseSim?.name ||
      (s.bess_sizing_simulation ? `Simulation #${s.bess_sizing_simulation}` : `Simulation #${s.id}`);
    return baseName.replace(/_hybrid$/i, "");
  };

  if (loadingList) {
    return (
      <div className="flex-1 flex flex-col justify-center items-center py-12 text-center text-slate-400 text-xs bess-card">
        <LoadingOutlined className="text-xl mb-2 text-[#0052CC] animate-spin" />
        <div>Loading hybrid simulation runs...</div>
      </div>
    );
  }

  const filteredSimulations = hybridSimulations
    .filter((sim) => {
      const query = searchQuery.trim().toLowerCase();
      if (!query) return true;
      const idStr = sim.id.toString();
      const hashIdStr = `#${sim.id}`;
      const sizingSimIdStr = sim.bess_sizing_simulation?.toString() || "";
      const sizingSimNameStr = (sim.bess_sizing_simulation_name || "").toLowerCase();
      return (
        idStr.includes(query) ||
        hashIdStr.includes(query) ||
        sizingSimIdStr.includes(query) ||
        sizingSimNameStr.includes(query)
      );
    })
    .sort((a, b) => {
      const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
      if (timeB !== timeA) return timeB - timeA;
      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });

  return (
    <div className="space-y-6">
      <div className="w-full max-w-xs relative mb-6">
        <input
          type="text"
          placeholder="Search simulations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-10 w-full pl-4 pr-10 border border-slate-200 rounded-lg text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-sm bg-white"
        />
        <SearchOutlined className="absolute right-3.5 top-3.5 text-slate-400 text-[13px]" />
      </div>

      {filteredSimulations.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSimulations.map((sim) => {
            const typeLabel = sim.simulation_type === 1 ? "Solar + BESS" : "Solar + Wind + BESS";
            const dateObj = sim.created_at ? new Date(sim.created_at) : null;
            const formattedDate = dateObj
              ? `${dateObj.toLocaleDateString()} ${dateObj.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`
              : "Draft (Not Run)";

            const leftBorderColor = sim.simulation_type === 1 ? "border-l-[#2563eb]" : "border-l-[#16a34a]";
            const badgeClass =
              sim.simulation_type === 1
                ? "bg-blue-50 text-blue-600 border border-blue-100"
                : "bg-emerald-50 text-emerald-600 border border-emerald-100";

            const hybridName = getHybridSimName(sim);

            return (
              <div
                key={sim.id}
                className={`p-6 border border-slate-200 border-l-4 ${leftBorderColor} rounded-2xl bg-white shadow-sm`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-slate-800 truncate" title={hybridName}>
                      {hybridName}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1 font-medium">{formattedDate}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${badgeClass}`}>
                      {typeLabel}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onDeleteSimulation) onDeleteSimulation(sim.id);
                      }}
                      className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-slate-50 cursor-pointer border-none bg-transparent flex items-center justify-center"
                      title="Delete Simulation"
                    >
                      <DeleteOutlined style={{ fontSize: "14px" }} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-slate-100 text-xs">
                  <div className="flex flex-col">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Tenure</span>
                    <span className="font-bold text-slate-800 text-[13px] mt-0.5">{sim.simulation_tenure} Yrs</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Evac Cap</span>
                    <span className="font-bold text-slate-800 text-[13px] mt-0.5">
                      {parseFloat(sim.evacuation_capacity_mva || 0).toFixed(1)} MVA
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Sizing Sim</span>
                    <span
                      className="font-bold text-slate-800 text-[13px] mt-0.5 truncate"
                      title={sim.bess_sizing_simulation_name || `#${sim.bess_sizing_simulation}`}
                    >
                      #{sim.bess_sizing_simulation}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-[30px]">
                  <button
                    type="button"
                    onClick={() => onLoadSimulation && onLoadSimulation(sim)}
                    className="w-full py-2 border border-blue-500 text-blue-600 bg-white hover:bg-blue-50/50 rounded-lg font-bold text-xs cursor-pointer transition-all shadow-sm flex items-center justify-center gap-1"
                  >
                    Load Config
                  </button>
                  <button
                    type="button"
                    onClick={() => onViewResults && onViewResults(sim.id)}
                    className="w-full py-2 text-white bg-[#0052cc] hover:bg-[#0747a6] rounded-lg font-bold text-xs cursor-pointer transition-all shadow-sm flex items-center justify-center gap-1 border-none"
                  >
                    View Results
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-white border border-slate-100 rounded-2xl text-slate-400 text-sm italic bess-card">
          {searchQuery.trim() ? "No simulations match your search query." : "No past sizing simulations found."}
        </div>
      )}
    </div>
  );
}
